<template>
  <div>
    <b-form
      ref="form">
      <b-card class="mt-2">
        <b-card
          title="Default View"
          class="mt-3">
          <b-row>
            <b-col cols="9">
              <b-form-group
                id="epg-default-view"
                description="Which EPG View should be the default">
                <b-form-select
                  id="epg-default-view"
                  v-model="config.defaultView"
                  required
                  :disabled="!canEdit"
                  :options="defaultViewOptions" />
              </b-form-group>
            </b-col>
            <b-col cols="3">
              <b-img
                fluid
                src="@/assets/images/epgdefaultview.jpg"
                alt="EPG Default View" />
            </b-col>
          </b-row>
        </b-card>
        <b-card
          title="Flip Time Configuration"
          class="mt-3">
          <flip-time
            v-if="config.flipTime"
            id="epg"
            ref="flipTimeEpg"
            v-model="config" />
        </b-card>
        <b-card
          title="Featured Ad Hoc Video Config"
          class="mt-3">
          <b-form-row>
            <b-col>
              <b-form-group
                id="enabled-group"
                label-for="enabled-button"
                description="Controls the visibility of the ad hoc stream promotion">
                <b-form-checkbox
                  v-if="config.featuredAdHocVideoConfig"
                  v-model="config.featuredAdHocVideoConfig.enabled"
                  :disabled="!canEdit"
                  name="enabled-button"
                  switch>
                  Enabled
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col>
              <b-form-group
                id="stream-url-group"
                label="Stream URL"
                label-for="stream-url-input"
                description="The URL for the ad hoc video stream Ex. https://.../*.m3u8">
                <b-form-input
                  v-if="config.featuredAdHocVideoConfig"
                  id="stream-url-input"
                  v-model="config.featuredAdHocVideoConfig.streamUrl"
                  :disabled="!canEdit"
                  :state="isValidUrl(config.featuredAdHocVideoConfig.streamUrl)"
                  type="url"
                  required
                  trim />
                <b-form-invalid-feedback id="stream-url-feedback">
                  Enter a valid URL
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col>
              <b-form-group
                id="asset-epg-url-group"
                label="Asset EPG URL"
                label-for="asset-epg-url-input"
                description="The URL for the app asset EPG Ex. https://.../*.pdf — Note: uploading a different file with the same name will OVERWRITE the existing file">
                <b-input-group>
                  <b-form-input
                    v-if="config.featuredAdHocVideoConfig"
                    id="asset-epg-url-input"
                    ref="assetEpgUrlInput"
                    v-model="config.featuredAdHocVideoConfig.appAssets.epg"
                    :disabled="!canEdit"
                    :state="isValidUrl(config.featuredAdHocVideoConfig.appAssets.epg)"
                    type="url"
                    required
                    trim />
                  <b-input-group-append>
                    <b-btn
                      variant="outline-primary"
                      @click.prevent="$refs.assetUploadModal.show()">
                      <i class="fas fa-plus" />
                    </b-btn>
                  </b-input-group-append>
                  <b-form-invalid-feedback id="asset-epg-url-feedback">
                    Enter a valid URL
                  </b-form-invalid-feedback>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-row v-if="showAssetPreview">
            <b-col>
              <label>Asset EPG Preview</label>
              <div v-if="config.featuredAdHocVideoConfig && isPdf(config.featuredAdHocVideoConfig.appAssets.epg)">
                <div style="width:50%">
                  <b-progress
                    v-if="assetPreviewLoadedRatio > 0 && assetPreviewLoadedRatio < 1"
                    :value="assetPreviewLoadedRatio * 100"
                    show-value
                    show-progress
                    animated />
                  <pdf
                    :src="config.featuredAdHocVideoConfig.appAssets.epg"
                    @error="onPdfError"
                    @progress="assetPreviewLoadedRatio = $event" />
                </div>
              </div>
              <div v-else-if="config.featuredAdHocVideoConfig && isImage(config.featuredAdHocVideoConfig.appAssets.epg)">
                <b-img :src="config.featuredAdHocVideoConfig.appAssets.epg" />
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-card>
    </b-form>
    <b-modal
      id="asset-upload"
      ref="assetUploadModal"
      hide-header
      hide-footer
      size="lg">
      <asset-upload
        post-action="/api/configuration/v1/epgcontent/assets"
        @asset-url-selected="onAssetSelect"
        @cancel="$refs.assetUploadModal.hide()" />
    </b-modal>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import pdf from 'vue-pdf'
import AssetUpload from '@/components/AssetUpload'
import FlipTime from "@/components/CalendarConfiguration/FlipTime"

export default {
  name: "EpgDisplayVue",
  components: {
    AssetUpload,
    FlipTime,
    pdf,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    canPublish: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    assetPreviewLoadedRatio: 0,
    defaultViewOptions: [{text: "MLB.TV", value: "mlb.tv"}, {text: "Featured (Subscriber VOD)", value: "svod"}],
  }),
  computed: {
    ...mapGetters([
      'betaEnvName',
      'liveEnvName',
      'isAdmin',
    ]),
    showAssetPreview() {
      if (this.config && this.config.featuredAdHocVideoConfig) {
        return this.config.featuredAdHocVideoConfig.appAssets.epg
      } else {
        return null
      }
    },
  },
  async created() {
    this.validated = true
  },
  methods: {
    isValid() {
      return (this.isValidUrl(this.config.featuredAdHocVideoConfig.streamUrl) &&
          this.isValidUrl(this.config.featuredAdHocVideoConfig.appAssets.epg))
    },
    onAssetSelect(url) {
      this.$refs.assetUploadModal.hide()

      if (url && this.config && this.config.featuredAdHocVideoConfig) {
        this.config.featuredAdHocVideoConfig.appAssets.epg = url
      } else {
        console.warn('Ignoring invalid asset selection')
      }
    },
    onPdfError(error) {
      console.error('Unable to load asset EPG preview pdf', error)
    },
    isPdf(file) {
      return file.split('.').pop() === 'pdf'
    },
    isImage(file) {
      return /.(jpg|jpeg|png|gif)$/i.test(file)
    },
    isValidUrl(url) {
      const valid = /^(http|https):\/\/[^ "]+$/i.test(url)
      if (!valid) {
        this.validated = false
      }
      return valid
    },
  },
}
</script>

<style scoped>

</style>